import React, { useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { supabase } from "../../lib/supabaseClient";
import { trackAttendanceCompleted } from "../../services/trackingService";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  targets: { userId: string; name?: string; avatarUrl?: string | null }[];
  currentUserId: string;
  isBotCreated?: boolean;
};

export default function PostEventRatingPrompt({ isOpen, onClose, eventId, targets, currentUserId, isBotCreated = false }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [scores, setScores] = useState<Record<string, number>>({});

  if (!isOpen) return null;

  // Block rating UI for bot-created Vybes
  if (isBotCreated) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="bg-theme-cards border border-theme-cards rounded-2xl shadow-card w-full max-w-md overflow-hidden"
        >
          <div className="p-5 border-b border-white/10">
            <h3 className="text-lg font-semibold">Rating Not Available</h3>
          </div>

          <div className="p-5">
            <p className="text-white/70 text-center">
              Ratings are only available for Vybes hosted by real users.
            </p>
          </div>

          <div className="p-5 border-t border-white/10">
            <button
              onClick={onClose}
              className="w-full py-2 rounded-xl bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 transition"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const setScore = (uid: string, s: number) => setScores(prev => ({ ...prev, [uid]: s }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const rows = Object.entries(scores)
        .filter(([, s]) => s > 0)
        .map(([rateeId, stars]) => ({ event_id: eventId, rater_id: currentUserId, ratee_id: rateeId, stars }));
      if (rows.length) {
        const { error } = await supabase.from("event_ratings").insert(rows);
        if (error) throw error;

        // Track attendance_completed for B2B analytics - rating implies attendance
        trackAttendanceCompleted(eventId, currentUserId);
      }
      onClose();
    } catch (e) {
      console.error(e);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-theme-cards border border-theme-cards rounded-2xl shadow-card w-full max-w-md overflow-hidden"
      >
        <div className="p-5 border-b border-white/10">
          <h3 className="text-lg font-semibold">Did they pass the Vybe Check? 💫</h3>
          <p className="text-sm text-white/70">Rate your Tribe members to help them earn their Vybe Check badge!</p>
        </div>

        <div className="p-5 space-y-4 max-h-[400px] overflow-y-auto">
          {targets.map(t => (
            <div key={t.userId} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
                    {t.avatarUrl ? <img src={t.avatarUrl} alt={t.name ?? "User"} className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="font-medium">{t.name ?? "Member"}</div>
                </div>
                <div className="flex">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} onClick={() => setScore(t.userId, n)} className="p-1">
                      <Star className={n <= (scores[t.userId] ?? 0) ? "fill-yellow-400 text-yellow-400" : "text-white/40"} size={20} />
                    </button>
                  ))}
                </div>
              </div>
              {scores[t.userId] >= 4 && (
                <div className="text-xs text-green-400 ml-13">
                  ✅ Positive vybe — helps their Vybe Check!
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="p-5 border-t border-white/10 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 rounded-xl border border-white/20 hover:bg-white/10 transition">Later</button>
          <button
            disabled={submitting}
            onClick={submit}
            className="flex-1 py-2 rounded-xl bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 transition disabled:opacity-50"
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
